import { type Dispatch, type SetStateAction } from 'react'
import moment from 'moment-timezone'
import classNames from 'classnames'
import { text } from './Constants'
import './VisitSummary.scss'
import 'react-calendar/dist/Calendar.css'
import { DATE_DAY_AND_TIME_WITH_DAY_FORMAT_EXTENDED } from '../../../../../../utils/dateUtils'
import RadioButtonsGroup from '../../../../../../components/radio-buttons-group/RadioButtonsGroup'
import { ProgramPlanBadge } from '@/components/program-plan-badge/ProgramPlanBadge'

/** One method choice on the reschedule radio group. */
type RadioOption = { value: string; isHidden?: boolean; isChecked?: boolean }

type Props = {
  fullName: string
  dob: string
  phoneNumber: string
  email: string
  address: string
  dateTime: string | Date
  providerName: string
  visitType: string
  visitMethod: string
  bookingState: string
  timeZone: string
  zoneAbbrString: string
  isReschedulePage: boolean
  selectedOption: RadioOption[]
  setSelectedOption: Dispatch<SetStateAction<RadioOption[]>>
  /** The patient the visit is for, so the program chip can say whether they are on a plan. */
  patientId: string | null | undefined
}

export const VisitSummary = ({
  fullName,
  dob,
  phoneNumber,
  email,
  address,
  dateTime,
  providerName,
  visitType,
  visitMethod,
  bookingState,
  timeZone,
  zoneAbbrString,
  isReschedulePage,
  selectedOption,
  setSelectedOption,
  patientId,
}: Props) => (
  <div
    className={classNames('review-book-visit-summary-container', {
      'review-book-visit-summary-container--reschedule': isReschedulePage,
    })}>
    {/* Title row carries the program chip so staff see the patient is on a plan
        without leaving the booking flow. The chip gates itself on the flag and on
        the patient holding a usable plan, so it renders nothing for everyone else. */}
    <div className='flex items-center gap-sp-1'>
      <label htmlFor='null' className='review-book-visit-summary-title'>
        {text.VISIT_SUMMARY}
      </label>
      <ProgramPlanBadge patientId={patientId} />
    </div>
    {isReschedulePage ?
      <label htmlFor='null' className='review-book-visit-summary-text'>
        {text.PLEASE_REVIEW_CHANGES}
      </label>
    : null}
    <label htmlFor='null' className='review-book-visit-summary-text bold'>
      {text.PATIENT_INFORMATION}
    </label>
    <label htmlFor='null' className='review-book-visit-summary-text'>
      {`${text.FULL_NAME}${fullName}`}
    </label>
    <label htmlFor='null' className='review-book-visit-summary-text'>
      {`${text.DOB}${dob}`}
    </label>
    <label htmlFor='null' className='review-book-visit-summary-text'>
      {`${text.PHONE_NUMBER}${phoneNumber}`}
    </label>
    <label htmlFor='null' className='review-book-visit-summary-text'>
      {`${text.EMAIL}${email}`}
    </label>
    <label htmlFor='null' className='review-book-visit-summary-text'>
      {`${text.ADDRESS}${address}`}
    </label>
    <label htmlFor='null' className='review-book-visit-summary-text bold'>
      {text.VISIT_INFORMATION}
    </label>
    <label htmlFor='null' className='review-book-visit-summary-text'>
      {`${text.DATE_TIME}${moment
        .tz(dateTime, timeZone)
        .format(DATE_DAY_AND_TIME_WITH_DAY_FORMAT_EXTENDED)} (${zoneAbbrString})`}
    </label>
    <label htmlFor='null' className='review-book-visit-summary-text'>
      {`${text.PROVIDER}${providerName}`}
    </label>
    <label htmlFor='null' className='review-book-visit-summary-text'>
      {`${text.VISIT_TYPE}${visitType}`}
    </label>
    {!isReschedulePage ?
      <>
        <label htmlFor='null' className='review-book-visit-summary-text'>
          {`${text.VISIT_METHOD}${visitMethod}`}
        </label>

        <label htmlFor='null' className='review-book-visit-summary-text'>
          {`${text.BOOKING_STATE}${bookingState}`}
        </label>
      </>
    : <>
        <label htmlFor='null' className='review-book-visit-summary-text'>
          {text.VISIT_METHOD}
        </label>
        <RadioButtonsGroup
          // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
          className='availability-repeats-radio-buttons-group'
          setSelectedOption={setSelectedOption}
          optionsList={selectedOption}
          useCustomLogic
        />
      </>
    }
  </div>
)
